import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Check, Clock, Globe, Shield, Sparkles, AlertCircle, MessageCircle, Mail } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Card } from '../../components/ui/card';

/**
 * Public-facing activation page — no auth.
 * Reached via WhatsApp Template #1 link → /activate/<token>.
 *
 * Renders:
 *  - Lead's business name + website preview link
 *  - Package inclusions + timeline + pricing breakdown
 *  - "Pay full ₹6,999 in advance" toggle
 *  - CTA → opens Razorpay Checkout
 */
const fmtINR = (n) => `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const loadRazorpayScript = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement('script');
  s.src = 'https://checkout.razorpay.com/v1/checkout.js';
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

export default function ActivationPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);   // { code, message } | null
  const [payFull, setPayFull] = useState(false);
  const [paying, setPaying] = useState(false);
  // Public company contact info — sourced from Settings → Company
  // Information. Always loaded (independent of the token resolution) so
  // the ErrorCard has real WhatsApp/email links even when the token is
  // deleted/expired.
  const [supportContact, setSupportContact] = useState(null);

  // Fetch company support contacts independently of the activation
  // payload so the ErrorCard isn't gated on a successful token resolve.
  useEffect(() => {
    let alive = true;
    axios.get(`${BASE}/api/smb/public/support-contact`)
      .then(r => { if (alive && r.data?.success) setSupportContact(r.data.data); })
      .catch(() => {/* fall back to placeholders in the ErrorCard */});
    return () => { alive = false; };
  }, []);

  // Fetch the activation payload. Robust against the entire failure
  // matrix — 404 (deleted/missing lead), 410 (expired token), network
  // errors, and malformed bodies. The previous catch handler `await`'d
  // r.json() without its own try-block, so any parse error would leave
  // `loading` stuck at true (issue reported by ops: deleted lead =
  // infinite loader).
  useEffect(() => {
    const ctrl = new AbortController();
    let alive = true;
    (async () => {
      try {
        // axios (XHR-based) sidesteps a platform-injected fetch wrapper
        // (emergent-main.js) that consumed the body stream and caused a
        // false "Failed to fetch / body stream already read" error,
        // which previously flipped the page to the network-error state
        // even when the backend returned a clean 404 for deleted leads.
        const r = await axios.get(`${BASE}/api/smb/activate/${token}`, {
          signal: ctrl.signal,
          validateStatus: () => true,
        });
        if (!alive) return;
        if (r.status >= 200 && r.status < 300) {
          setData(r.data);
        } else {
          const code = r.status;
          const detail = r.data?.detail || '';
          const message =
            code === 404
              ? (detail ||
                 'This activation link is no longer valid. The link may have been removed or the lead is no longer active.')
              : code === 410
              ? (detail || 'This activation link has expired. Please request a fresh link.')
              : (detail || `Couldn't load the activation page (error ${code}).`);
          setError({ code, message });
        }
      } catch (e) {
        if (!alive || axios.isCancel(e) || e?.name === 'CanceledError' || e?.name === 'AbortError') return;
        console.error('Activation fetch failed:', e);
        setError({
          code: 0,
          message: "Couldn't reach our servers. Check your internet and refresh the page.",
        });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [token]);

  const handlePay = async () => {
    setPaying(true);
    const sdkOk = await loadRazorpayScript();
    if (!sdkOk) { setPaying(false); setError({ code: 'sdk', message: 'Payment SDK failed to load. Check your internet.' }); return; }
    try {
      const resp = await fetch(`${BASE}/api/smb/activate/${token}/checkout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pay_full: payFull }),
      });
      if (!resp.ok) {
        const j = await resp.json();
        throw new Error(j.detail || 'Could not initialise payment');
      }
      const co = await resp.json();
      const rzp = new window.Razorpay({
        key: co.key_id,
        amount: co.amount,
        currency: co.currency,
        order_id: co.order_id,
        // Payee + UPI memo come from server (admin-configurable; Myzow
        // brand by default — NOT the SMB's business name).
        name: co.name || 'Myzow Solutions LLP',
        description: co.description || (payFull ? 'Website — Full Payment' : 'Website Activation Fee'),
        prefill: co.prefill,
        theme: { color: '#22c55e' },
        // callback_url + redirect:true are CRITICAL for mobile UPI/GPay
        // flows — when the browser is backgrounded while GPay opens, the
        // JS `handler` callback below often never fires. Razorpay's
        // server-side redirect to callback_url is the only reliable
        // post-payment hand-off in that case.
        callback_url: co.callback_url,
        redirect: !!co.callback_url,
        handler: () => {
          // Razorpay handler fires on customer's device when payment succeeds.
          // The authoritative confirmation comes via webhook → smb_projects row.
          // Poll status briefly, then navigate to the form.
          const pollStart = Date.now();
          const poll = async () => {
            try {
              const r = await fetch(`${BASE}/api/smb/activate/${token}/status`);
              const j = await r.json();
              if (j.status === 'activation_paid' || j.status === 'details_submitted') {
                navigate(`/activate/${token}/form`); return;
              }
            } catch {/* ignore */}
            if (Date.now() - pollStart < 30000) setTimeout(poll, 1500);
            else navigate(`/activate/${token}/form`);  // navigate anyway; form will gate on status
          };
          poll();
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.open();
    } catch (e) {
      setError({ code: 'payment', message: e.message });
      setPaying(false);
    }
  };

  if (loading) return <CenteredLoader />;
  if (error) return <ErrorCard error={error} contact={supportContact} />;
  if (!data) return null;

  const pkg = data.package;
  const amount = payFull ? pkg.total_amount : pkg.activation_amount;

  if (data.lead.is_paid) {
    return (
      <Container>
        <Card className="p-8 text-center space-y-4">
          <Check className="h-12 w-12 mx-auto text-emerald-500" />
          <h1 className="text-2xl font-bold">Payment already received</h1>
          <p className="text-muted-foreground">You can now complete the activation form.</p>
          <Button onClick={() => navigate(`/activate/${token}/form`)} data-testid="goto-form">Continue to form</Button>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <header className="text-center mb-8">
        <p className="text-sm uppercase tracking-widest text-emerald-600 font-semibold">Activation</p>
        <h1 className="text-3xl sm:text-5xl font-bold mt-2">Let&apos;s make your website live</h1>
        <p className="text-muted-foreground mt-2">Hello {data.lead.contact_name || data.lead.business_name}, here&apos;s what&apos;s included.</p>
      </header>

      {data.lead.preview_url && (
        <Card className="p-4 mb-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Globe className="h-5 w-5 text-emerald-500 shrink-0" />
            <div>
              <p className="text-sm font-medium">Your website preview is ready</p>
              <p className="text-xs text-muted-foreground truncate max-w-[180px] sm:max-w-none">{data.lead.preview_url}</p>
            </div>
          </div>
          <a href={data.lead.preview_url} target="_blank" rel="noreferrer" data-testid="open-preview">
            <Button variant="outline" size="sm">View preview</Button>
          </a>
        </Card>
      )}

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <Card className="p-5 space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold"><Sparkles className="h-4 w-4 text-emerald-500" />Package inclusions</div>
          <ul className="space-y-2 text-sm">
            {(pkg.inclusions || []).map((inc, i) => (
              <li key={i} className="flex gap-2"><Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />{inc}</li>
            ))}
          </ul>
        </Card>
        <Card className="p-5 space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold"><Clock className="h-4 w-4 text-emerald-500" />Project timeline</div>
          <p className="text-sm">{pkg.timeline_days || 7}-day delivery window starting on payment confirmation.</p>
          <div className="flex items-center gap-2 text-sm font-semibold pt-2"><Shield className="h-4 w-4 text-emerald-500" />Review rounds</div>
          <p className="text-sm">Up to {pkg.revisions_max || 2} review rounds included in this package.</p>
        </Card>
      </div>

      <Card className="p-6 mb-6 bg-emerald-500/5 border-emerald-500/30">
        <p className="text-sm font-semibold mb-3">Pricing</p>

        {/* Anchor framing — shows the market rate as a strikethrough above
            the actual package price. Sourced from pkg.anchor_amount with
            a ₹12,000 fallback for packages that pre-date the field. Ops
            can set anchor_amount=0 to hide this block entirely. */}
        {(() => {
          const anchor = pkg.anchor_amount === 0 ? 0 : Number(pkg.anchor_amount || 12000);
          if (!anchor) return null;
          const youSave = Math.max(0, anchor - Number(pkg.total_amount || 0));
          return (
            <div className="space-y-1.5 text-sm mb-3 pb-3 border-b border-emerald-500/30">
              <div className="flex items-center justify-between text-muted-foreground">
                <span>Professional websites typically start from</span>
                <span className="font-mono line-through" data-testid="pricing-anchor">{fmtINR(anchor)}+</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-medium flex items-center gap-2">
                  Your Website Package
                  <span className="text-[10px] bg-amber-500/15 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded-full font-semibold border border-amber-500/40"
                        data-testid="pricing-offer-badge">
                    LIMITED LAUNCH OFFER
                  </span>
                </span>
                <span className="font-mono font-bold text-emerald-600 text-lg" data-testid="pricing-package">{fmtINR(pkg.total_amount)}</span>
              </div>
              {youSave > 0 && (
                <div className="flex items-center justify-between text-emerald-600 dark:text-emerald-400 text-xs">
                  <span>You save</span>
                  <span className="font-mono font-semibold" data-testid="pricing-savings">~{fmtINR(youSave)}</span>
                </div>
              )}
            </div>
          );
        })()}

        <div className="space-y-1.5 text-sm">
          <Row label="Total Package" value={fmtINR(pkg.total_amount)} />
          <Row label="Activation Amount" value={fmtINR(pkg.activation_amount)} accent />
          <Row label="Balance Before Launch" value={fmtINR(pkg.balance_amount)} muted />
        </div>
        <hr className="my-4 border-emerald-500/30" />
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium">Pay full {fmtINR(pkg.total_amount)} in advance</p>
            <p className="text-xs text-muted-foreground">Skip the back-and-forth at launch.</p>
          </div>
          <Switch checked={payFull} onCheckedChange={setPayFull} data-testid="pay-full-toggle" />
        </div>
      </Card>

      <Card className="p-4 mb-6 flex items-start gap-3 bg-amber-500/5 border-amber-500/30">
        <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
        <p className="text-xs">Domain availability is checked after you submit the activation form. We&apos;ll confirm and proceed with your preferred domain.</p>
      </Card>

      <Button
        onClick={handlePay}
        disabled={paying || !data.razorpay_configured}
        size="lg"
        className="w-full text-base h-14 bg-emerald-500 hover:bg-emerald-600"
        data-testid="activate-cta"
      >
        {paying ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Opening secure payment…</>
                : `Activate My Website for ${fmtINR(amount)}`}
      </Button>
      {!data.razorpay_configured && (
        <p className="text-xs text-center mt-2 text-rose-500">Payment gateway not configured. Contact support.</p>
      )}
    </Container>
  );
}

const Container = ({ children }) => (
  <div className="min-h-screen bg-background py-10 px-4">
    <div className="max-w-2xl mx-auto">{children}</div>
  </div>
);

const Row = ({ label, value, accent, muted }) => (
  <div className={`flex items-center justify-between ${muted ? 'text-muted-foreground' : ''}`}>
    <span>{label}</span>
    <span className={`font-mono ${accent ? 'font-bold text-emerald-600 text-base' : ''}`}>{value}</span>
  </div>
);

const CenteredLoader = () => (
  <Container>
    <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-500" /></div>
  </Container>
);

const ErrorCard = ({ error, contact }) => {
  const isMissing = error?.code === 404;
  const isExpired = error?.code === 410;
  // Pull WhatsApp + email from Settings → Company Information. Strip
  // non-digit characters from the WhatsApp number so the wa.me URL is
  // well-formed regardless of what format ops stored ("+91 99999 99999"
  // vs raw "919999999999").
  const SUPPORT_EMAIL = (contact?.email || '').trim();
  const rawWa = (contact?.whatsapp || contact?.phone || '').trim();
  const SUPPORT_WHATSAPP = rawWa.replace(/[^\d]/g, '');
  const headline = isExpired
    ? 'This link has expired'
    : isMissing
    ? 'Activation link unavailable'
    : "Couldn't load the page";
  return (
    <Container>
      <Card className="p-8 text-center space-y-4" data-testid="activation-error-card">
        <AlertCircle className="h-12 w-12 mx-auto text-rose-500" />
        <h1 className="text-2xl font-bold">{headline}</h1>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {error?.message || 'Something went wrong.'}
        </p>
        {(SUPPORT_WHATSAPP || SUPPORT_EMAIL) ? (
          <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
            {SUPPORT_WHATSAPP && (
              <a href={`https://wa.me/${SUPPORT_WHATSAPP}?text=Hi%2C%20my%20activation%20link%20is%20not%20working.%20Can%20you%20help%3F`}
                 target="_blank" rel="noreferrer" data-testid="error-whatsapp">
                <Button className="bg-emerald-500 hover:bg-emerald-600 w-full sm:w-auto">
                  <MessageCircle className="h-4 w-4 mr-2" />
                  WhatsApp Support
                </Button>
              </a>
            )}
            {SUPPORT_EMAIL && (
              <a href={`mailto:${SUPPORT_EMAIL}?subject=Activation%20link%20issue&body=Hi%20team%2C%20my%20activation%20link%20isn%27t%20working.`}
                 data-testid="error-email">
                <Button variant="outline" className="w-full sm:w-auto">
                  <Mail className="h-4 w-4 mr-2" />
                  Email Support
                </Button>
              </a>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground pt-2" data-testid="error-no-contact">
            Please reply to the WhatsApp message you received from us — we&apos;ll help you out.
          </p>
        )}
      </Card>
    </Container>
  );
};
